import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const ANLASS_OPTIONS = [
  "Hochzeit", "Geburtstag", "Jubiläum", "Polterabend", "Vereinsfeier",
  "Weihnachtsfeier", "Geschäftsfeier", "Betriebsfest", "Produktpräsentation",
  "Konzert", "Disco", "Theater", "Film & Foto", "Ausstellung",
  "Kunstveranstaltung", "Tagung", "Workshop", "Messe", "Sonstiges",
];

const PERSONEN_OPTIONS = ["bis 20", "21–50", "51–75", "76–100"];

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.5 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

export default function Contact() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", anlass: "",
    datum: "", personen: "", nachricht: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    toast.success("Anfrage gesendet! Wir melden uns bald.", {
      description: "Demo-Modus: Keine echte E-Mail wird versendet.",
      duration: 6000,
    });
    setTimeout(() => setSubmitted(false), 3000);
  };

  const input =
    "w-full bg-transparent border-b border-[#F2EDE8]/15 focus:border-[#C1440E] outline-none py-3 text-[#F2EDE8] placeholder-[#A19D98]/50 text-sm transition-colors duration-200";
  const label = "text-[#A19D98] text-[10px] tracking-[0.3em] uppercase block mb-2";

  return (
    <motion.main
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      data-testid="contact-page"
    >
      <div className="pt-32 pb-10 px-6 md:px-12 max-w-7xl mx-auto">
        <p
          className="text-[#C1440E] text-xs tracking-[0.4em] uppercase mb-4"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Kontakt
        </p>
        <h1
          className="text-5xl md:text-7xl font-extrabold uppercase text-[#F2EDE8] mb-5"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="contact-title"
        >
          Termin anfragen
        </h1>
        <a
          href="tel:02349350002"
          className="flex items-center gap-4 text-[#D4A843] hover:text-[#C1440E] transition-colors duration-200 group w-fit"
          data-testid="contact-phone-big"
        >
          <Phone size={22} />
          <span
            className="text-3xl md:text-5xl font-light"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            0234 / 935 000 2
          </span>
        </a>
      </div>

      <div className="px-6 md:px-12 max-w-7xl mx-auto pb-24">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20">
          {/* Left – Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex flex-col gap-8"
          >
            <div className="flex flex-col gap-5">
              <a
                href="tel:02349350002"
                className="flex items-start gap-4 group"
                data-testid="contact-info-phone"
              >
                <div className="w-10 h-10 bg-[#1C1C1E] border border-[#F2EDE8]/10 flex items-center justify-center flex-shrink-0 group-hover:border-[#C1440E] transition-colors">
                  <Phone size={15} className="text-[#C1440E]" />
                </div>
                <div>
                  <p className={label} style={{ fontFamily: "'Outfit', sans-serif" }}>Telefon</p>
                  <p className="text-[#F2EDE8] text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    0234 / 935 000 2
                  </p>
                </div>
              </a>
              <a
                href="mailto:info@bochum-partyraum.de"
                className="flex items-start gap-4 group"
                data-testid="contact-info-email"
              >
                <div className="w-10 h-10 bg-[#1C1C1E] border border-[#F2EDE8]/10 flex items-center justify-center flex-shrink-0 group-hover:border-[#C1440E] transition-colors">
                  <Mail size={15} className="text-[#C1440E]" />
                </div>
                <div>
                  <p className={label} style={{ fontFamily: "'Outfit', sans-serif" }}>E-Mail</p>
                  <p className="text-[#F2EDE8] text-sm break-all" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    info@bochum-partyraum.de
                  </p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#1C1C1E] border border-[#F2EDE8]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin size={15} className="text-[#C1440E]" />
                </div>
                <div>
                  <p className={label} style={{ fontFamily: "'Outfit', sans-serif" }}>Adresse</p>
                  <p className="text-[#F2EDE8] text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Schattbachstraße 77<br />44803 Bochum (Laer)
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1C1C1E] p-6 border border-[#F2EDE8]/5">
              <p
                className="text-[#D4A843] text-xs tracking-widest uppercase mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Anfahrt
              </p>
              <p className="text-[#A19D98] text-sm leading-relaxed mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <span className="text-[#F2EDE8]">Per Auto:</span> A43, Ausfahrt Bochum-Laer,
                dann Beschilderung nach Laer folgen. Schattbachstraße 77.
              </p>
              <p className="text-[#A19D98] text-sm leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <span className="text-[#F2EDE8]">ÖPNV:</span> Bochum Hbf, Bus Richtung Laer.
                Haltestelle Laer Mitte.
              </p>
            </div>

            <div
              className="overflow-hidden border border-[#F2EDE8]/10"
              data-testid="contact-map"
            >
              <iframe
                src="https://www.google.com/maps?q=Schattbachstra%C3%9Fe+77,+44803+Bochum,+Germany&output=embed"
                width="100%"
                height="300"
                style={{
                  border: 0,
                  filter: "invert(90%) hue-rotate(180deg) grayscale(0.3) brightness(0.8)",
                  display: "block",
                }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Atelier 77 – Standort Bochum-Laer"
              />
            </div>
          </motion.div>

          {/* Right – Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            data-testid="contact-form-container"
          >
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-7"
              data-testid="contact-form"
            >
              <div>
                <label className={label} style={{ fontFamily: "'Outfit', sans-serif" }}>Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Ihr vollständiger Name"
                  className={input}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="form-name"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <div>
                  <label className={label} style={{ fontFamily: "'Outfit', sans-serif" }}>Telefon</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="Ihre Telefonnummer"
                    className={input}
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid="form-phone"
                  />
                </div>
                <div>
                  <label className={label} style={{ fontFamily: "'Outfit', sans-serif" }}>E-Mail *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Ihre E-Mail-Adresse"
                    className={input}
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid="form-email"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <div>
                  <label className={label} style={{ fontFamily: "'Outfit', sans-serif" }}>Anlass</label>
                  <select
                    name="anlass"
                    value={form.anlass}
                    onChange={handleChange}
                    className={`${input} cursor-pointer bg-[#141414]`}
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid="form-anlass"
                  >
                    <option value="">Bitte wählen</option>
                    {ANLASS_OPTIONS.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={label} style={{ fontFamily: "'Outfit', sans-serif" }}>Wunschdatum</label>
                  <input
                    type="date"
                    name="datum"
                    value={form.datum}
                    onChange={handleChange}
                    className={`${input} cursor-pointer`}
                    style={{ fontFamily: "'Outfit', sans-serif", colorScheme: "dark" }}
                    data-testid="form-datum"
                  />
                </div>
              </div>
              <div>
                <label className={label} style={{ fontFamily: "'Outfit', sans-serif" }}>Personenanzahl</label>
                <select
                  name="personen"
                  value={form.personen}
                  onChange={handleChange}
                  className={`${input} cursor-pointer bg-[#141414]`}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="form-personen"
                >
                  <option value="">Bitte wählen</option>
                  {PERSONEN_OPTIONS.map((p) => (
                    <option key={p} value={p}>{p} Personen</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label} style={{ fontFamily: "'Outfit', sans-serif" }}>Nachricht</label>
                <textarea
                  name="nachricht"
                  value={form.nachricht}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Erzählen Sie uns von Ihrem Event..."
                  className={`${input} resize-none`}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="form-nachricht"
                />
              </div>
              <button
                type="submit"
                disabled={submitted}
                className="flex items-center justify-center gap-3 bg-[#C1440E] hover:bg-[#D4A843] text-white hover:text-[#141414] py-4 transition-all duration-300 disabled:opacity-50"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: "13px", letterSpacing: "0.15em", textTransform: "uppercase" }}
                data-testid="contact-submit-btn"
              >
                {submitted ? "Anfrage gesendet!" : (<>Anfrage senden <ArrowRight size={15} /></>)}
              </button>
              <p
                className="text-[#A19D98]/40 text-xs text-center"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Demo-Formular · Keine echte Datenübertragung
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </motion.main>
  );
}
